import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { MarketplaceResultType } from "config";
import CompanyProfileSchema from "marketplace/companies/company-profile-schema";
import IndividualSchema from "marketplace/individuals/_schemas/individual-schema";
import type { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import TeamProfileSchema from "marketplace/team-profile/_schema/team-profile-schema";
import { z } from "zod";

export type TeamJSON = z.infer<typeof TeamSchema.TeamJSONZ>;
export type TeamParams = z.infer<typeof TeamSchema.TeamZ>;

export default class TeamSchema {
  public static readonly TeamJSONZ = z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string().nullish(),
    contactUserId: z.uuid().nullish(),
    categories: z.array(z.string()).nullish(),
    avatarId: z.uuid().nullish(),
    leader: IndividualSchema.IndividualJSONZ,
    isVisible: z.boolean(),
    members: z.array(IndividualSchema.IndividualJSONZ),
    associatedFirms: z.array(CompanyProfileSchema.CompanyProfileJSONZ),
  });
  public static readonly TeamZ = z.object({
    id: z.instanceof(Guid),
    leader: IndividualSchema.IndividualZ,
    profile: TeamProfileSchema.TeamProfileZ,
    isVisible: z.boolean(),
    members: z.array(IndividualSchema.IndividualZ),
    associatedFirms: z.array(CompanyProfileSchema.CompanyProfileZ),
  });

  public static validate(values: unknown): TeamParams {
    return parseOrThrow(TeamSchema.TeamZ, values, {
      logError: true,
      schemaName: "TeamSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): TeamJSON {
    return parseOrThrow(TeamSchema.TeamJSONZ, values, {
      logError: true,
      schemaName: "TeamSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static validateJSONArray(values: unknown): TeamJSON[] {
    return parseOrThrow(z.array(TeamSchema.TeamJSONZ), values, {
      logError: true,
      schemaName: "TeamSchema",
      operation: "validateJSONArray",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is TeamParams {
    try {
      TeamSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is TeamJSON {
    try {
      TeamSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: TeamJSON): TeamParams {
    const parsed = parseOrThrow(TeamSchema.TeamJSONZ, data, {
      logError: true,
      schemaName: "TeamSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return TeamSchema.codec.decode(parsed);
  }

  public static serialize(document: TeamParams): TeamJSON {
    const parsed = TeamSchema.validate(document);
    return TeamSchema.codec.encode(parsed);
  }

  public static fromMarketplaceSearchInfo(result: MarketplaceSearchInfoJSON): TeamJSON | undefined {
    if (result.type !== MarketplaceResultType.Team) {
      return;
    }

    const parsed = TeamSchema.TeamJSONZ.safeParse({
      ...result,
      id: result.id,
      name: result.name,
      leader: result.leader,
      members: result.members ?? [],
      associatedFirms: result.associatedFirms ?? [],
      isVisible: result.isVisible ?? false,
    });

    return parsed.success ? parsed.data : undefined;
  }

  private static readonly codec = z.codec(TeamSchema.TeamJSONZ, TeamSchema.TeamZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id)!,
      leader: IndividualSchema.deserialize(data.leader),
      profile: TeamProfileSchema.deserialize({
        name: data.name,
        avatarId: data.avatarId,
        categories: data.categories,
        description: data.description,
        contactUserId: data.contactUserId,
      }),
      isVisible: data.isVisible,
      members: data.members.map(IndividualSchema.deserialize),
      associatedFirms: data.associatedFirms.map(CompanyProfileSchema.deserialize),
    }),
    encode: (team) => ({
      id: team.id.toJSON(),
      leader: IndividualSchema.serialize(team.leader),
      name: team.profile.name,
      description: team.profile.description ?? null,
      contactUserId: team.profile.contactUserId?.toJSON() ?? null,
      categories: team.profile.categories ?? null,
      avatarId: team.profile.avatarId?.toJSON() ?? null,
      isVisible: team.isVisible,
      members: team.members.map(IndividualSchema.serialize),
      associatedFirms: team.associatedFirms.map(CompanyProfileSchema.serialize),
    }),
  });
}
